import React, { useEffect, useState } from "react";
import { instrumentName } from "../../utils/instrumentFormat";
import { assetClass, priceDecimals } from "../../utils/orderFormat";
import { squareExportPanelHeight } from "./squareExport";

// Simple hook that doesn't create its own WebSocket to avoid connection limits
const useSharedPrice = (ticker) => {
  const [price, setPrice] = useState(null);
  const [bid, setBid] = useState(null);
  const [ask, setAsk] = useState(null);

  // Reset price, bid, ask when ticker changes
  useEffect(() => {
    setPrice(null);
    setBid(null);
    setAsk(null);
  }, [ticker]);

  useEffect(() => {
    // Listen for live price updates from the main chart (only for price, NOT bid/ask)
    const handleLivePrice = (event) => {
      if (event.detail && typeof event.detail.price === 'number') {
        // Store the RAW live price — display precision (asset-class, e.g. 4dp for
        // forex / sub-$1 crypto) is applied at render, not flattened to 2dp here.
        setPrice(event.detail.price);
        // Do NOT set bid/ask here - wait for quotesUpdate from BuySellFunction
      }
    };

    // Listen for quotes update from BuySellFunction (uses exact same bid/ask as header)
    const handleQuotesUpdate = (event) => {
      if (event.detail) {
        if (event.detail.bid) {
          setBid(event.detail.bid);
        }
        if (event.detail.ask) {
          setAsk(event.detail.ask);
        }
      }
    };

    window.addEventListener('livePriceUpdate', handleLivePrice);
    window.addEventListener('quotesUpdate', handleQuotesUpdate);

    return () => {
      window.removeEventListener('livePriceUpdate', handleLivePrice);
      window.removeEventListener('quotesUpdate', handleQuotesUpdate);
    };
  }, []);

  return { price, bid, ask };
};


// Formats an overlay's parameter string for display.
//
// Client, 7. Standardize parameter formatting: "Use spaces after commas
// everywhere: BB (20, 2) / PPO (12, 26, 9)". The user types "20,2" and the raw
// string was echoed straight into the label, so the two label families disagreed
// with each other and with the client's spec.
export const formatOverlayParams = (raw) => {
  if (raw === undefined || raw === null) return "";
  return String(raw)
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p !== "")
    .join(", ");
};

// The overlay legend (BB / SMA / EMA / PSAR), rendered as a normal block INSIDE
// the price-summary stack rather than as an absolutely positioned overlay.
//
// Client, 5. Fix the mobile overlay position: "The mobile BB label is still
// floating far down inside the chart. Anchor it directly beneath the asset price
// and return block with about 12 pixels of space. If two overlays are active,
// stack the second directly underneath with 6 to 8 pixels between them. This
// should use the same compact positioning logic as desktop, not a vertical
// position calculated from the chart height."
//
// That last sentence is the fix. The legend used to live in Chart.js at
// `position: absolute; top: <magic number>` — 74px on desktop, 124px on mobile —
// numbers chosen to clear whatever the summary happened to be at the time. On a
// phone the summary is shorter than 124px, so the label floated in open chart
// space; and any change to the rows above it silently broke the spacing again.
//
// Rendered here it is simply the next child after the price / SELL-BUY block, so
// it sits 12px under whatever that block actually is, on every breakpoint, with
// no number to keep in sync. Both the mobile and desktop branches use this one
// component so they cannot drift.
//
// Client, 4. Standardize all chart labels: "Use Poppins at 14px, weight 500,
// with the existing muted gray for BB, RSI, ADL, PPO, and OBV." The family is
// stated explicitly rather than inherited so this label cannot pick up a
// different stack from an ancestor; #6B7280 is the `secondary` token, the same
// one the indicator PANEL labels use (Chart.js, indicatorPanelLabel).
export const OverlayLegend = ({ items, mobileView }) => {
  if (!items || items.length === 0) return null;
  return (
    <div
      className="flex flex-col"
      style={{
        // Desktop's parent carries gap-1 (4px), so 8 here lands on the 12px the
        // client asked for. Mobile's parent has no gap, so it takes the full 12.
        marginTop: mobileView ? 12 : 8,
        // "stack the second directly underneath with 6 to 8 pixels between them"
        gap: 8,
        alignSelf: "start",
        backdropFilter: "blur(1px)",
      }}
    >
      {items.map((item, index) => (
        <div key={index} className="flex items-center legend-items">
          <span
            className="legend-item-color"
            style={{
              display: "block",
              width: mobileView ? 12 : 14,
              height: 2,
              borderRadius: 1,
              marginRight: 6,
              backgroundColor: item.color, // system-assigned, unchanged
            }}
          />
          <span
            className="legend-item-text"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: 1.2,
              color: "#6B7280",
            }}
          >
            {item.label}
            {item.value ? ` (${formatOverlayParams(item.value)})` : ""}
          </span>
        </div>
      ))}
    </div>
  );
};

// The active overlays, in the shape OverlayLegend wants. Same source and same
// rules Chart.js used before the legend moved here: only rows that have both a
// type and a parameter, and whatever colour the system already assigned.
const OVERLAY_LABELS = { bb: "BB", ema: "EMA", psar: "PSAR", sma: "SMA" };
export const activeOverlayLegendItems = (setting) => {
  const params = setting?.overlayParams;
  if (!Array.isArray(params)) return [];
  return params
    .filter((o) => o && o.selectValue && o.inputValue && OVERLAY_LABELS[o.selectValue])
    .map((o) => ({
      label: OVERLAY_LABELS[o.selectValue],
      color: o.color,
      value: o.inputValue,
    }));
};

export function ChartTopSummary(props) {
  const {
    mobileView,
    stockData,
    tickerFromLocalStorage,
    setting,
    stockPrice,
    indicatorConfig,
    squareExport,
  } = props;
const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Push the summary down past any indicator panels that render above the main
  // price chart, so it always anchors to the top-left of the price panel.
  const aboveIndicatorCount = Array.isArray(indicatorConfig)
    ? indicatorConfig.filter(
        (ind) => ind && ind.position === "above" && ind.selectValue !== ""
      ).length
    : 0;
  const belowIndicatorCount = Array.isArray(indicatorConfig)
    ? indicatorConfig.filter(
        (ind) => ind && ind.position === "below" && ind.selectValue !== ""
      ).length
    : 0;
  // During the mobile square share the panels shrink, so the price block's
  // offset follows the same panel height Chart.js uses.
  const indicatorPanelHeight =
    squareExport && mobileView
      ? squareExportPanelHeight(squareExport.height, aboveIndicatorCount + belowIndicatorCount, 150)
      : mobileView ? 150 : 180;
  const aboveIndicatorOffset = aboveIndicatorCount * indicatorPanelHeight;

  // console.log("🎯 ChartTopSummary component rendered with stockData:", stockData?.ticker);

  // Active overlays for the legend rendered under the price block (see
  // OverlayLegend above). Derived from `setting`, which this component already
  // receives, so the legend needs no state of its own.
  const overlayLegendItems = activeOverlayLegendItems(setting);

  const [initialPrice, setInitialPrice] = useState(stockData?.ticker?.price);
  const [currentTicker, setCurrentTicker] = useState(stockData?.ticker?.ticker);
  const { price, bid, ask } = useSharedPrice(currentTicker);

  // State for crosshair OHLC display on mobile
  const [crosshairActive, setCrosshairActive] = useState(false);
  const [ohlcData, setOhlcData] = useState(null);

  // Reset price to API response when stockData changes
  useEffect(() => {
    console.log("🎯 ChartTopSummary useEffect - stockData changed:", stockData?.ticker?.ticker, stockData?.ticker?.price);
    if (stockData?.ticker?.price !== undefined) {
      setInitialPrice(stockData.ticker.price);
    }
    if (stockData?.ticker?.ticker) {
      setCurrentTicker(stockData.ticker.ticker);
    }
  }, [stockData, stockData?.ticker?.ticker, stockData?.ticker?.price]);

  // Listen for crosshair state changes (both mobile and desktop)
  useEffect(() => {
    const handleCrosshairChange = (event) => {
      const { isActive, ohlcData: data } = event.detail;
      setCrosshairActive(isActive);
      if (data) {
        setOhlcData(data);
      }
    };

    const handleCrosshairDataChange = (event) => {
      const { ohlcData: data } = event.detail;
      if (data) {
        setOhlcData(data);
      }
    };

    window.addEventListener('crosshairStateChange', handleCrosshairChange);
    window.addEventListener('crosshairDataChange', handleCrosshairDataChange);

    return () => {
      window.removeEventListener('crosshairStateChange', handleCrosshairChange);
      window.removeEventListener('crosshairDataChange', handleCrosshairDataChange);
    };
  }, []);

  // Use live price when available (from websocket), fallback to initialPrice (from API).
  // Every price the header shows (price, SELL/BUY, OHLC, change) is formatted to the
  // ASSET CLASS's precision: forex → 4dp, crypto → 4dp under $1 / 2dp at-or-over $1,
  // stocks → 2dp. So TRON reads 0.3300 and EURUSD reads 1.1400, not 0.33 / 1.14 — and
  // a stock never shows the raw feed's 1–3 decimals either.
  const headerSymbol =
    stockData?.ticker?.ticker || tickerFromLocalStorage || setting?.symbol || "";
  const fmtPrice = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toFixed(priceDecimals(assetClass(headerSymbol), n))
      : "";
  };
  const rawDisplayPrice = price || initialPrice;
  const displayPrice = fmtPrice(rawDisplayPrice);
  // For SELL/BUY boxes: bid for SELL, ask for BUY (fallback to displayPrice if not available)
  const sellPrice = fmtPrice(bid || rawDisplayPrice);
  const buyPrice = fmtPrice(ask || rawDisplayPrice);
  // Client: "A price and a % change taken from different numbers is a bug." The
  // price above streams live while the API's todaysChange was frozen at page load
  // (and, after hours, measured from a different number than the price). So the
  // change is derived from the price actually shown, against the API's previous
  // close; the API's own values are only a fallback when that close is missing.
  const prevClose = Number(stockData?.ticker?.prev_close);
  const shownPrice = Number(rawDisplayPrice);
  const hasBase = prevClose > 0 && shownPrice > 0;
  const dayChange = hasBase ? shownPrice - prevClose : stockData?.ticker?.todaysChange;
  const dayChangePct = hasBase
    ? ((shownPrice - prevClose) / prevClose) * 100
    : stockData?.ticker?.todaysChangePerc;
  const dayChangePctText = Number.isFinite(Number(dayChangePct))
    ? Number(dayChangePct).toFixed(2)
    : "0.00";
  const changeColor = dayChange < 0 ? "#ef5350" : "#26a69a";

  // Mobile view layout - overlay inside chart on top-left
  if (mobileView) {
    return (
      <div
        className="flex flex-col px-2 py-1"
        style={{
          position: "absolute",
          top: aboveIndicatorOffset,
          left: 0,
          zIndex: 2,
          //  backdropFilter: "blur(1px)",
          borderRadius: "0 0 8px 0",
          // Make the name/price overlay non-interactive so a long-press can't
          // select its text or raise the iOS Copy·Look Up·Translate callout —
          // touches pass through to the chart (same as the desktop branch). The
          // user-select rules are kept as a belt-and-suspenders.
          pointerEvents: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
        }}
      >

        {/* Row 1: Stock name and icon */}
        {/* <div className="flex items-center gap-2"       style={{
        backdropFilter: "blur(1px)" , width: "300px"
      }}>
          <span className="text-sm font-medium text-gray-800">
            {(stockData?.ticker?.name.split("(")[0]  || "N/A").trim()}
          </span>
        </div> */}

        {isMobileView ? (
          <div
            className="flex items-center gap-2"
            style={{ alignSelf: "start" }}
          >
            <span className="text-sm font-medium text-gray-800" style={{
              backdropFilter: "blur(1px)",
            }}>
              {instrumentName({ name: stockData?.ticker?.name }) || "N/A"}
            </span>
          </div>
        ) : (
          <div
            className="flex items-center gap-2"
            style={{ backdropFilter: "blur(1px)" , alignSelf: "start" }}
          >
            <span className="text-sm font-medium text-gray-800">
              {instrumentName({ name: stockData?.ticker?.name }) || "N/A"}
            </span>
          </div>
        )}

        {/* Row 2: When crosshair is NOT active - show price and change */}
        {!crosshairActive && (
          <div className="flex items-baseline gap-2 mt-0.5" style={{
         backdropFilter: "blur(1px)", alignSelf: "start"
      }}>
            <span className="text-xl font-bold text-gray-800 ">
              {displayPrice}
            </span>
            <span
              className="text-sm font-medium"
              style={{ color: changeColor }}
            >
              {dayChange >= 0 ? "+" : ""}
              {fmtPrice(dayChange) || "0.00"}
            </span>
            <span
              className="text-sm font-medium"
              style={{ color: changeColor }}
            >
              ({dayChangePct >= 0 ? "+" : ""}
              {dayChangePctText}%)
            </span>
          </div>
        )}

        {/* Row 2: When crosshair IS active - show OHLC data, price change, and current price */}
        {crosshairActive && ohlcData && (
          <div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[12px] leading-none"       style={{
        backdropFilter: "blur(1px)"
      }}>
              <span className="text-gray-500 dark:text-gray-400">
                O<span
                  className="ml-0.5 font-semibold"
                  style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}
                >
                  {fmtPrice(ohlcData.open)}
                </span>
              </span>

              <span className="text-gray-500 dark:text-gray-400">
                H<span
                  className="ml-0.5 font-semibold"
                  style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}
                >
                  {fmtPrice(ohlcData.high)}
                </span>
              </span>

              <span className="text-gray-500 dark:text-gray-400">
                L<span
                  className="ml-0.5 font-semibold"
                  style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}
                >
                  {fmtPrice(ohlcData.low)}
                </span>
              </span>

              <span className="text-gray-500 dark:text-gray-400">
                C<span
                  className="ml-0.5 font-semibold"
                  style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}
                >
                  {fmtPrice(ohlcData.close)}
                </span>
              </span>
            </div>

            {/* Row 3: Show today's change and percentage below OHLC */}
            <div className="flex items-center gap-2 mt-0.5"       style={{
        backdropFilter: "blur(1px)",
      }}>
              <span
                className="text-xs font-medium"
                style={{ color: changeColor }}
              >
                {dayChange >= 0 ? "+" : ""}
                {fmtPrice(dayChange) || "0.00"}
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: changeColor }}
              >
                ({dayChangePct >= 0 ? "+" : ""}
                {dayChangePctText}%)
              </span>
            </div>
            {/* Row 4: SELL and BUY boxes with bid/ask prices */}
            {/* Client "Trade Screen Launch Polish", 3. Sell and Buy quote
                controls: the hardcoded `0.00` that sat between the two boxes is
                gone (it was a literal, never a real value — nothing computed it
                and nothing explained it), and the pair now reads as one
                intentionally spaced unit at gap-2 instead of two boxes with a
                stray number wedged between them. No label replaces it. */}
            <div className="flex items-center gap-2 mt-1">
              <div className="flex flex-col items-center px-2 py-1 border border-red-500 rounded bg-white/90 backdrop-blur-sm">
                <span className="font-semibold text-red-500 text-xs">{sellPrice}</span>
                <span className="text-red-500 text-[10px]">SELL</span>
              </div>
              <div className="flex flex-col items-center px-2 py-1 border border-blue-500 rounded bg-white/90 backdrop-blur-sm">
                <span className="font-semibold text-blue-500 text-xs">{buyPrice}</span>
                <span className="text-blue-500 text-[10px]">BUY</span>
              </div>
            </div>
          </div>
        )}

        {/* Overlay legend — the next block after the price / OHLC / SELL-BUY
            rows, so it is anchored to them rather than to a chart-height
            number. See OverlayLegend. */}
        <OverlayLegend items={overlayLegendItems} mobileView />
      </div>
    );
  }

  // Desktop view layout - TradingView style overlay inside chart
  return (
    <div
      className="flex flex-col gap-1 px-3 py-2"
      style={{
        position: "absolute",
        top: aboveIndicatorOffset,
        left: 0,
        zIndex: 3,
        borderRadius: "0 0 8px 0",
        pointerEvents: "none",
      }}
    >

{isMobileView ? (
  <div
    className="flex items-center gap-3"
    style={{
      backdropFilter: "blur(1px)",
      width: "300px"
    }}
  >
    <span className="text-sm font-medium text-gray-800">
      {instrumentName({ name: stockData?.ticker?.name }) || "N/A"}
    </span>

    <span className="text-xs text-gray-500 dark:text-gray-400">
      {stockData?.ticker?.ticker ||
        tickerFromLocalStorage ||
        setting?.symbol ||
        "NA"}
    </span>

    {ohlcData && (
      <>
        <span className="text-gray-500 dark:text-gray-400 text-sm">
          O
          <span
            className="ml-1 font-semibold"
            style={{
              color:
                ohlcData.close >= ohlcData.open
                  ? "#26a69a"
                  : "#ef5350",
            }}
          >
            {fmtPrice(ohlcData.open)}
          </span>
        </span>

        <span className="text-gray-500 dark:text-gray-400 text-sm">
          H
          <span
            className="ml-1 font-semibold"
            style={{
              color:
                ohlcData.close >= ohlcData.open
                  ? "#26a69a"
                  : "#ef5350",
            }}
          >
            {fmtPrice(ohlcData.high)}
          </span>
        </span>

        <span className="text-gray-500 dark:text-gray-400 text-sm">
          L
          <span
            className="ml-1 font-semibold"
            style={{
              color:
                ohlcData.close >= ohlcData.open
                  ? "#26a69a"
                  : "#ef5350",
            }}
          >
            {fmtPrice(ohlcData.low)}
          </span>
        </span>

        <span className="text-gray-500 dark:text-gray-400 text-sm">
          C
          <span
            className="ml-1 font-semibold"
            style={{
              color:
                ohlcData.close >= ohlcData.open
                  ? "#26a69a"
                  : "#ef5350",
            }}
          >
            {fmtPrice(ohlcData.close)}
          </span>
        </span>
      </>
    )}

    <span className="text-sm font-semibold" style={{ color: changeColor }}>
      {dayChange >= 0 ? "+" : ""}
      {fmtPrice(dayChange) || "0.00"}
    </span>

    <span className="text-sm font-semibold" style={{ color: changeColor }}>
      ({dayChangePct >= 0 ? "+" : ""}
      {dayChangePctText}%)
    </span>
  </div>
) : (
  <div
    className="flex items-center gap-3"
    style={{ backdropFilter: "blur(1px)" }}
  >
    <span className="text-sm font-medium text-gray-800">
      {instrumentName({ name: stockData?.ticker?.name }) || "N/A"}
    </span>

    {/* everything else stays exactly same */}
    <span className="text-xs text-gray-500 dark:text-gray-400">
      {stockData?.ticker?.ticker ||
        tickerFromLocalStorage ||
        setting?.symbol ||
        "NA"}
    </span>

    {ohlcData && (
      <>
                  <>
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              O<span className="ml-1 font-semibold" style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}>{fmtPrice(ohlcData.open)}</span>
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              H<span className="ml-1 font-semibold" style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}>{fmtPrice(ohlcData.high)}</span>
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              L<span className="ml-1 font-semibold" style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}>{fmtPrice(ohlcData.low)}</span>
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              C<span className="ml-1 font-semibold" style={{ color: ohlcData.close >= ohlcData.open ? '#26a69a' : '#ef5350' }}>{fmtPrice(ohlcData.close)}</span>
            </span>
          </>
      </>
    )}

    <span className="text-sm font-semibold" style={{ color: changeColor }}>
      {dayChange >= 0 ? "+" : ""}
      {fmtPrice(dayChange) || "0.00"}
    </span>

    <span className="text-sm font-semibold" style={{ color: changeColor }}>
      ({dayChangePct >= 0 ? "+" : ""}
      {dayChangePctText}%)
    </span>
  </div>
)}
      {/* Row 1: Stock name + OHLC + price change */}


      {/* Row 2: SELL/BUY boxes */}
      {/* Same change as the mobile branch above: the unexplained `0.00` between
          SELL and BUY is removed and the gap closed to gap-2 so the two quote
          controls read as one deliberately spaced pair. */}
      <div className="flex items-center gap-2 w-fit"   style={{
          backdropFilter: "blur(1px)"
        }}>
        <div className="flex flex-col items-center px-2 py-0.5 border border-red-500 rounded text-center min-w-[60px] bg-white/90 backdrop-blur-sm">
          <span className="font-semibold text-red-500 text-xs">{sellPrice}</span>
          <span className="text-red-500 text-[10px]">SELL</span>
        </div>
        <div className="flex flex-col items-center px-2 py-0.5 border border-teal-500 rounded text-center min-w-[60px] bg-white/90 backdrop-blur-sm">
          <span className="font-semibold text-teal-500 text-xs">{buyPrice}</span>
          <span className="text-teal-500 text-[10px]">BUY</span>
        </div>
      </div>

      {/* Overlay legend — sits directly under the SELL/BUY pair. Same component
          and same rules as the mobile branch above. */}
      <OverlayLegend items={overlayLegendItems} />
    </div>
  );
}
